import { useState, useEffect, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { Avatar, Dialog, Rules } from "../../components";
import { games } from "../../lib/catalogue";
import { playerName, type GameLayoutProps } from "../types";
import { GameIcon } from "./GameIcons";
import { usePrivatePanel } from "./usePrivatePanel";
import { PrivatePanel, PrivateRevealContext } from "./PrivatePanel";
import { HistoryPanel } from "./HistoryPanel";
import { PublicDisplay } from "./PublicDisplay";
import { publicSeatName, publicTableModel } from "../public-presentation";
import { useTurnSignal } from "./useTurnSignal";
import {
  ActionStatusContext,
  useActionStatus,
  useSendingStage,
} from "./ActionStatus";
/** Countdown chip for server-timed result phases. The ring shrinks from the time
 * left when the deadline was first received; the number is the real remainder.
 * The ticking text is hidden from assistive technology; the layout announces
 * the beat once. */
function Countdown({
  seconds,
  fraction,
  short = false,
}: {
  seconds: number;
  fraction: number;
  /** Beside the host's "Next": the ring and seconds only. */
  short?: boolean;
}) {
  const c = 2 * Math.PI * 7;
  return (
    <span className="play-chip play-auto">
      <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
        <circle cx="9" cy="9" r="7" className="ring-track" />
        <circle
          cx="9"
          cy="9"
          r="7"
          className="ring-fill"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.max(0, Math.min(1, fraction)))}
        />
      </svg>
      <span aria-hidden="true">
        {short ? `${seconds}s` : `Next in ${seconds}s`}
      </span>
    </span>
  );
}
/** Viewport able to hold the inline hand without covering the bid controls. A
 * typical phone browser (≈393×660 with Safari's bars) qualifies; genuinely short
 * windows and an open software keyboard fall back to the dialog. */
const INLINE_QUERY = "(min-width:340px) and (min-height:600px)";
function inlineAvailable() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia(INLINE_QUERY).matches &&
    (!window.visualViewport ||
      window.visualViewport.height >= window.innerHeight - 100)
  );
}
export function GameLayout({
  state,
  send,
  disabled,
  conceal = false,
  isDisplay,
  title,
  description,
  board,
  action,
  privateContent,
  privateAction,
  history,
  phaseLabel,
  phaseNote,
  privatePresentation = "dialog",
  privateLabel = "Your card",
  privateInlineContent,
  composition = "split",
  contextArt,
  publicTitle,
  publicDescription,
  publicBoard,
  privateScope = "phase",
  privateCoverNote,
  unrevealedTitle,
  progressLabel = "submitted",
  pending = false,
  error = null,
}: GameLayoutProps) {
  const g = state.game,
    p = g.public,
    players = p.players ?? [];
  const [overlay, setOverlay] = useState<"people" | "history" | "rules" | null>(
    null,
  );
  const [canInline, setCanInline] = useState(inlineAvailable);
  useEffect(() => {
    // Native viewport events are outside React's event boundary. Commit the
    // presentation guard before the browser can paint the old revealed surface.
    const update = () => flushSync(() => setCanInline(inlineAvailable()));
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);
  const adaptive = privatePresentation === "adaptive-inline";
  const privatePanel = usePrivatePanel({
    matchId: state.matchId,
    // A finished match always gets a fresh identity so no card stays open over the result.
    phaseId: g.finished
      ? "finished"
      : privateScope === "match"
        ? "match"
        : privateScope === "round"
          ? `round:${g.round}`
          : (state.phaseId ?? p.phaseKey),
    conceal: conceal || state.paused,
    roomInstanceId: state.roomInstanceId,
    playerId: state.playerId,
    presentation: adaptive && canInline ? "inline" : "dialog",
  });
  const publicAudience = isDisplay || state.isSpectator;
  const nightPublic = g.id === "mafia" && g.phase === "night";
  const sharedDisplay = Boolean(state.isDisplay);
  const own = !publicAudience && Boolean(state.playerId);
  const triggerRef = useRef<HTMLElement | null>(null);
  const reveal = () => {
    triggerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setOverlay(null);
    privatePanel.reveal();
  };
  const closePrivate = () => {
    privatePanel.cover();
    requestAnimationFrame(() => {
      if (triggerRef.current?.isConnected) triggerRef.current.focus();
    });
  };
  const show = (which: typeof overlay) => {
    privatePanel.cover();
    setOverlay(which);
  };
  const [now, setNow] = useState(Date.now());
  const receivedAt = useMemo(() => Date.now(), [state.serverNow]);
  const totalMs = useMemo(
    () =>
      state.automaticAdvanceAt
        ? Math.max(1, state.automaticAdvanceAt - state.serverNow)
        : 1,
    [state.automaticAdvanceAt],
  );
  useEffect(() => {
    if (!state.automaticAdvanceAt) return;
    const t = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(t);
  }, [state.automaticAdvanceAt]);
  const remainingMs = state.automaticAdvanceAt
    ? Math.max(
        0,
        state.automaticAdvanceAt - state.serverNow - Math.max(0, now - receivedAt),
      )
    : null;
  const remaining =
    remainingMs === null ? null : Math.ceil(remainingMs / 1000);
  // Announce the automatic beat once, when its deadline is first received.
  const [beatAnnouncement, setBeatAnnouncement] = useState("");
  useEffect(() => {
    if (!state.automaticAdvanceAt) {
      setBeatAnnouncement("");
      return;
    }
    const seconds = Math.max(
      1,
      Math.round((state.automaticAdvanceAt - state.serverNow) / 1000),
    );
    setBeatAnnouncement(`Next in ${seconds} seconds`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.automaticAdvanceAt]);
  const gameName = games.find((v) => v.id === g.id)?.name ?? "Shared board";
  const model = publicTableModel(g, state.players ?? []);
  const shownPhaseLabel =
    phaseLabel ?? `${g.id === "mafia" ? "Day" : "Round"} ${g.round}`;
  const publicHeadline = publicTitle ?? gameName;
  const announcement = useTurnSignal({
    state,
    isDisplay,
    phaseTitle: shownPhaseLabel,
    publicTitle: publicHeadline,
  });
  // Public: the seat holding the phase's public actor field is disconnected.
  const actorSeat = model.seats.find((s) => s.id === model.actorId);
  const actorOffline =
    actorSeat && actorSeat.connected === false && actorSeat.id !== state.playerId
      ? `${publicSeatName(model.seats, actorSeat.id)} is offline${
          state.paused && /reconnect/i.test(state.pauseReason ?? "")
            ? " · paused until they’re back"
            : ""
        }`
      : null;
  const hostNext =
    own &&
    Boolean(state.playerId) &&
    state.playerId === state.hostId &&
    Boolean(state.automaticAdvanceAt) &&
    !state.paused;
  const stage = useSendingStage(pending);
  const actionStatus = useActionStatus(stage);
  // A rejected action shows next to its control until the player changes
  // something or the next attempt resolves.
  const [dismissed, setDismissed] = useState(false);
  const wasPending = useRef(pending);
  useEffect(() => {
    const resolved = wasPending.current && !pending;
    wasPending.current = pending;
    if (resolved) setDismissed(false);
  }, [pending]);
  useEffect(() => setDismissed(false), [error]);
  const shownError = own && error && !dismissed ? error : null;
  // An action taken inside the private card reports its rejection there only:
  // at Mafia night the covered public surface must not change by role.
  const errorInPrivate =
    Boolean(privateAction) || (g.id === "mafia" && g.phase === "night");
  const dismissError = () => {
    if (shownError) setDismissed(true);
  };
  const errorLine = shownError ? (
    <p className="play-action-error" role="alert">
      <GameIcon name="alert" size={18} />
      <span>{shownError}</span>
    </p>
  ) : null;
  const opened = privatePanel.opened;
  const ownTitle =
    own && unrevealedTitle && !opened && privateContent ? unrevealedTitle : title;
  const nudge = Boolean(
    own && unrevealedTitle && !opened && privateContent,
  );
  const shownDescription = publicAudience ? publicDescription : description;
  const context = (
    <header className="play-context" data-tour="phase">
      {contextArt}
      <div className="play-phase">
        <span className="play-chip play-phase-chip">{shownPhaseLabel}</span>
        {phaseNote ? (
          <span className="play-chip" data-tour="phase-note">
            {phaseNote}
          </span>
        ) : null}
        {state.paused ? (
          <span className="play-chip is-paused">
            <GameIcon name="pause" size={14} />
            Paused
          </span>
        ) : null}
        {remaining !== null && remainingMs !== null ? (
          <span className="play-beat" data-tour="countdown">
            <Countdown
              seconds={remaining}
              fraction={remainingMs / totalMs}
              short={hostNext}
            />
            {hostNext ? (
              <button
                type="button"
                className="play-next"
                data-tour="next"
                disabled={disabled}
                onClick={() =>
                  send({ type: "room.advance", phaseId: state.phaseId })
                }
              >
                Next
                <GameIcon name="next" size={14} />
              </button>
            ) : null}
          </span>
        ) : null}
        <span className="sr-only" role="status">
          {beatAnnouncement}
        </span>
      </div>
      <h1 data-tour="title">{publicAudience ? publicHeadline : ownTitle}</h1>
      {shownDescription ? (
        <div className="play-description">{shownDescription}</div>
      ) : null}
      {actorOffline ? (
        <p className="play-offline-note">
          <i className="seat-offline-dot" aria-hidden="true" />
          {actorOffline}
        </p>
      ) : null}
    </header>
  );
  const revealedRole = (id: string) =>
    p.revealedRoles?.find((x: any) => x.playerId === id)?.role as
      | string
      | undefined;
  // One private entry: the inline tray where it fits, the dock pill otherwise.
  const inlineEntry = own && privateContent && adaptive && canInline;
  const dockEntry = own && privateContent && !inlineEntry;
  return (
    <PrivateRevealContext.Provider value={reveal}>
      <ActionStatusContext.Provider value={actionStatus}>
        <div
          data-game={g.id}
          className={`play-shell ${g.id} phase-${g.phase} ${g.finished ? "is-finished" : ""}${publicAudience ? " is-public" : ""}${sharedDisplay ? " is-display" : ""}${state.paused ? " is-paused" : ""}`}
        >
          <p className="sr-only" role="status" aria-live="polite">
            {announcement}
          </p>
          {publicAudience ? (
            <PublicDisplay
              board={publicBoard ?? board}
              context={context}
              model={model}
              progressLabel={progressLabel}
              spotlight={!g.finished}
            />
          ) : (
            <div
              className={`play-stage${composition === "stacked" ? " is-stacked" : " is-split"}`}
            >
              {composition === "stacked" ? context : null}
              <section
                className="play-table"
                aria-label="Game board"
                data-tour="board"
              >
                {board}
              </section>
              <section
                className={`play-action-sheet${own && (action || (shownError && !errorInPrivate)) ? " has-action" : ""}`}
                data-tour="action-sheet"
              >
                {composition === "split" ? context : null}
                {inlineEntry ? (
                  <section
                    className="play-private-inline"
                    aria-label={`${privateLabel}, covered`}
                  >
                    {privatePanel.surface === "inline" && privatePanel.open ? (
                      <div
                        className="play-private-content"
                        data-private-surface="inline"
                        data-tour="private-card"
                      >
                        {privateInlineContent ?? privateContent}
                        <button
                          type="button"
                          className="secondary"
                          onClick={closePrivate}
                        >
                          Hide
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`secondary full play-reveal-tray${nudge ? " is-nudge" : ""}`}
                        data-tour="private-entry"
                        disabled={conceal || state.paused}
                        onClick={reveal}
                      >
                        <GameIcon name="cards" size={20} />
                        <span className="play-reveal-label">
                          Reveal {privateLabel.toLowerCase()}
                          {privateCoverNote && !opened ? (
                            <small>{privateCoverNote}</small>
                          ) : null}
                        </span>
                      </button>
                    )}
                  </section>
                ) : null}
                {own && action ? (
                  <fieldset
                    className="play-action"
                    aria-label="Your action"
                    data-tour="action"
                    // Mafia night: a special role's pending private action must not grey the
                    // public card entry, which villagers never see change. Only role-independent
                    // states (offline, paused) may disable it.
                    disabled={nightPublic ? conceal || state.paused : disabled}
                    aria-busy={nightPublic ? undefined : disabled}
                    onChangeCapture={dismissError}
                    onInputCapture={dismissError}
                    onClickCapture={dismissError}
                  >
                    {action}
                    {errorInPrivate ? null : errorLine}
                  </fieldset>
                ) : errorInPrivate ? null : (
                  errorLine
                )}
              </section>
            </div>
          )}
          {sharedDisplay ? null : (
            <nav className="play-tools" aria-label="Game tools" data-tour="dock">
              {(
                [
                  ["rules", "Rules"],
                  ["people", "People"],
                  ["history", "History"],
                ] as const
              ).map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => show(key)}
                  data-tour={`tool-${key}`}
                >
                  <GameIcon name={key} />
                  <span>{label}</span>
                </button>
              ))}
              {dockEntry ? (
                <button
                  type="button"
                  className={`play-tools-private${nudge ? " is-nudge" : ""}${privateCoverNote && !opened ? " is-fresh" : ""}`}
                  data-tour="private-entry"
                  disabled={conceal || state.paused}
                  onClick={reveal}
                >
                  <GameIcon name="cards" />
                  <span>{privateLabel}</span>
                  {privateCoverNote && !opened ? (
                    <span className="sr-only">, {privateCoverNote}</span>
                  ) : null}
                </button>
              ) : null}
            </nav>
          )}
          {own && privatePanel.open && privatePanel.surface === "dialog" ? (
            <PrivatePanel onClose={closePrivate}>
              <div className="play-private-tools">
                <button className="text-button" onClick={() => show("rules")}>
                  Rules
                </button>
                <button
                  className="text-button"
                  onClick={() => show("history")}
                >
                  History
                </button>
              </div>
              <div className="private-card" data-tour="private-card">
                <span className="private-corner" aria-hidden="true">
                  ✳
                </span>
                {privateContent}
                <span className="private-corner is-end" aria-hidden="true">
                  ✳
                </span>
              </div>
              {privateAction ? (
                <fieldset
                  className="play-action"
                  data-tour="private-action"
                  disabled={disabled}
                  onChangeCapture={dismissError}
                  onClickCapture={dismissError}
                >
                  {privateAction}
                  {errorLine}
                </fieldset>
              ) : null}
            </PrivatePanel>
          ) : null}
          {overlay === "people" ? (
            <Dialog title="At the table" onClose={() => setOverlay(null)}>
              <ul className="play-people">
                {players.map((v: any, i: number) => {
                  const role = revealedRole(v.id);
                  const connected = state.players?.find(
                    (c: any) => c.id === v.id,
                  )?.connected;
                  const dice = p.diceCounts?.find(
                    (x: any) => x.playerId === v.id,
                  )?.count;
                  return (
                    <li
                      key={v.id}
                      className={v.alive === false ? "is-out" : undefined}
                    >
                      <Avatar name={v.name} index={i} />
                      <span className="people-name">
                        <strong>{playerName(players, v.id)}</strong>
                        <span className="people-tags">
                          {v.id === state.playerId ? (
                            <span className="play-chip is-accent">You</span>
                          ) : null}
                          {v.alive === false ? (
                            <span className="play-chip">Out</span>
                          ) : null}
                          {connected === false ? (
                            <span className="play-chip">Offline</span>
                          ) : null}
                          {role ? (
                            <span className="play-chip is-role">
                              {role.replaceAll("-", " ")}
                            </span>
                          ) : null}
                          {g.id === "liar-dice" ? (
                            <span className="play-chip">
                              {dice ?? 0} {dice === 1 ? "die" : "dice"}
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Dialog>
          ) : null}
          {overlay === "history" ? (
            <Dialog title="Game history" onClose={() => setOverlay(null)}>
              {history ?? <HistoryPanel game={g} />}
            </Dialog>
          ) : null}
          {overlay === "rules" ? (
            <Rules
              game={games.find((x) => x.id === g.id)!}
              onClose={() => setOverlay(null)}
            />
          ) : null}
        </div>
      </ActionStatusContext.Provider>
    </PrivateRevealContext.Provider>
  );
}
